import { useSWRConfig } from 'swr'
import useSWRMutation from 'swr/mutation'
import { auditsApi } from '../api/index.ts'
import type { AuditSummary, CreateAuditInput, QuotaPatch, StageRunScope } from '../types.ts'
import { auditKeys } from './audits.keys.ts'

/** Creating an audit repopulates the list; the wizard navigates to the page. */
export function useCreateAudit() {
  const { mutate } = useSWRConfig()
  return useSWRMutation(auditKeys.list, async (_key, { arg }: { arg: CreateAuditInput }) => {
    const result = await auditsApi.create(arg)
    if (!result.success) throw result.error
    await mutate(auditKeys.list)
    return result.data
  })
}

/** `scope ∈ all | failed | ids[]` with an optional `limit` (§10.6). */
export function useRunStage(auditId: string) {
  const { mutate } = useSWRConfig()
  return useSWRMutation(
    auditKeys.stages(auditId),
    async (
      _key,
      { arg }: { arg: { stage: string; scope: StageRunScope; ids?: string[]; limit?: number } },
    ) => {
      const result = await auditsApi.runStage(auditId, arg.stage, arg)
      if (!result.success) throw result.error
      await Promise.all([mutate(auditKeys.stages(auditId)), mutate(auditKeys.detail(auditId))])
      return result.data
    },
  )
}

/** `PATCH …/quota`: the file, the row, the log — and a paused audit resumes. */
export function useExtendQuota(auditId: string) {
  const { mutate } = useSWRConfig()
  return useSWRMutation(
    auditKeys.detail(auditId),
    async (_key, { arg }: { arg: QuotaPatch }): Promise<AuditSummary> => {
      const result = await auditsApi.patchQuota(auditId, arg)
      if (!result.success) throw result.error
      await Promise.all([mutate(auditKeys.detail(auditId)), mutate(auditKeys.list)])
      return result.data
    },
  )
}
